import { useState, useEffect } from 'react';
import db from '@/db';
import { Input } from '@/components/ui/input';
import { Barcode, FlaskConical, CheckCircle2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { parseScaleBarcode, autoDetectFormat } from '@/lib/barcode';

export default function ScaleBarcodeCard() {
  const [prefix, setPrefix] = useState('2');
  const [pluDigits, setPluDigits] = useState(5);
  const [mode, setMode] = useState('peso');
  const [testCode, setTestCode] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const [p, pd, m] = await Promise.all([
        db.config.get('scaleBarcodePrefix'),
        db.config.get('scaleBarcodePluDigits'),
        db.config.get('scaleBarcodeMode'),
      ]);
      if (p) setPrefix(p.value || '2');
      if (pd) setPluDigits(pd.value || 5);
      if (m) setMode(m.value || 'peso');
    })();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await db.config.put({ key: 'scaleBarcodePrefix', value: String(prefix || '2') });
      await db.config.put({ key: 'scaleBarcodePluDigits', value: Number(pluDigits) || 5 });
      await db.config.put({ key: 'scaleBarcodeMode', value: mode });
      toast.success('Configuração da balança salva');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = () => {
    if (!testCode || testCode.length < 8) {
      toast.error('Informe um código de barras de teste');
      return;
    }
    const cfg = { prefix, pluDigits: Number(pluDigits), mode };
    const r = parseScaleBarcode(testCode, cfg);
    setTestResult(r);
    if (!r.ok) {
      // tenta auto-detect
      const auto = autoDetectFormat(testCode);
      if (auto) {
        toast.info(`Falhou com seu config — auto-detectado: PLU ${auto.plu.length} dig, modo ${auto.mode}`, {
          description: 'Considere ajustar configuração.',
        });
        setTestResult(auto);
      }
    }
  };

  const handleAutoDetect = () => {
    if (!testCode || testCode.length < 13) {
      toast.error('Informe um código de 13 dígitos para auto-detectar');
      return;
    }
    const auto = autoDetectFormat(testCode);
    if (!auto) {
      toast.error('Não foi possível auto-detectar');
      return;
    }
    setPluDigits(auto.plu.length);
    setMode(auto.mode);
    setPrefix(auto.prefix);
    setTestResult(auto);
    toast.success(`Detectado: PLU ${auto.plu.length} dig, modo ${auto.mode}, prefixo ${auto.prefix}`);
  };

  return (
    <div data-testid="settings-scale-barcode" className="bg-white border border-slate-200 rounded-2xl p-6">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
          <Barcode size={20} strokeWidth={1.75} />
        </div>
        <div>
          <h3 className="font-heading text-lg font-bold text-slate-900">Etiqueta da Balança (EAN-13)</h3>
          <p className="text-xs text-slate-500">Configure como a Ramuza Atena/Toledo codifica peso ou preço</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <div>
          <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 block mb-1.5">
            Prefixo
          </label>
          <Input
            data-testid="settings-barcode-prefix"
            value={prefix}
            onChange={(e) => setPrefix(e.target.value.replace(/\D/g, '').slice(0, 2))}
            placeholder="2"
            className="h-10 font-mono text-center font-bold border-2 border-slate-200 rounded-lg"
          />
          <p className="text-[10px] text-slate-400 mt-1">Padrão Brasil: <strong>2</strong></p>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 block mb-1.5">
            Dígitos do PLU
          </label>
          <select
            data-testid="settings-barcode-plu-digits"
            value={pluDigits}
            onChange={(e) => setPluDigits(Number(e.target.value))}
            className="h-10 w-full px-3 font-mono text-center font-bold border-2 border-slate-200 rounded-lg bg-white"
          >
            <option value={4}>4 dígitos</option>
            <option value={5}>5 dígitos</option>
            <option value={6}>6 dígitos</option>
          </select>
          <p className="text-[10px] text-slate-400 mt-1">Mais comum: <strong>5</strong></p>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 block mb-1.5">
            Modo
          </label>
          <select
            data-testid="settings-barcode-mode"
            value={mode}
            onChange={(e) => setMode(e.target.value)}
            className="h-10 w-full px-3 font-bold border-2 border-slate-200 rounded-lg bg-white"
          >
            <option value="peso">Peso (gramas)</option>
            <option value="preco">Preço (centavos)</option>
          </select>
          <p className="text-[10px] text-slate-400 mt-1">Use a etiqueta de teste abaixo</p>
        </div>
      </div>

      <button
        data-testid="settings-barcode-save"
        onClick={handleSave}
        disabled={saving}
        className="w-full sm:w-auto px-5 h-10 rounded-lg bg-slate-900 text-white text-sm font-bold hover:bg-slate-800 disabled:opacity-50 transition-colors"
      >
        {saving ? 'Salvando...' : 'Salvar configuração'}
      </button>

      <div className="mt-6 pt-5 border-t border-slate-100">
        <div className="flex items-center gap-2 mb-2">
          <FlaskConical size={14} className="text-amber-600" />
          <p className="text-[11px] uppercase tracking-wider font-bold text-amber-700">Testador de etiqueta</p>
        </div>
        <p className="text-xs text-slate-500 mb-3">
          Imprima uma etiqueta na balança, digite ou bipe os 13 dígitos do código de barras abaixo, e veja o que o sistema entende.
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input
            data-testid="settings-barcode-test-input"
            value={testCode}
            onChange={(e) => setTestCode(e.target.value.replace(/\D/g, '').slice(0, 13))}
            placeholder="Bipe aqui ou digite os 13 dígitos"
            className="h-10 font-mono tracking-wider border-2 border-slate-200 rounded-lg flex-1"
          />
          <button
            data-testid="settings-barcode-test-btn"
            onClick={handleTest}
            className="h-10 px-4 rounded-lg bg-blue-600 text-white text-sm font-bold hover:bg-blue-700"
          >
            Testar
          </button>
          <button
            data-testid="settings-barcode-autodetect-btn"
            onClick={handleAutoDetect}
            className="h-10 px-4 rounded-lg bg-amber-500 text-white text-sm font-bold hover:bg-amber-600"
          >
            Auto-detectar
          </button>
        </div>

        {testResult && (
          <div
            data-testid="settings-barcode-test-result"
            className={`mt-3 px-4 py-3 rounded-xl border-2 ${
              testResult.ok ? 'bg-emerald-50/70 border-emerald-200' : 'bg-rose-50 border-rose-200'
            }`}
          >
            {testResult.ok ? (
              <div className="flex items-start gap-2">
                <CheckCircle2 size={16} className="text-emerald-600 mt-0.5 shrink-0" />
                <div className="text-sm text-emerald-900 min-w-0">
                  <p className="font-bold">
                    {testResult.mode === 'peso'
                      ? `Peso: ${testResult.weightKg?.toFixed(3)} kg`
                      : `Preço total: R$ ${testResult.priceTotal?.toFixed(2).replace('.', ',')}`}
                  </p>
                  <p className="text-[11px] mt-0.5 opacity-80">
                    Prefixo <strong>{testResult.prefix}</strong> • PLU <strong>{testResult.plu}</strong>{' '}
                    • Check digit <strong>{testResult.checkDigit}</strong>{' '}
                    {testResult.checkValid ? '✓ válido' : '⚠️ divergente'}
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-2">
                <AlertTriangle size={16} className="text-rose-600 mt-0.5 shrink-0" />
                <p className="text-sm text-rose-900 font-semibold">{testResult.reason}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
